import axios from 'axios';
import { API_URL } from '../config';

// Create an axios instance with default config
const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a request interceptor to add the JWT token to requests
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Authentication services
export const authService = {
  // Register a new user
  register: async (email, password, phone_number, company_name) => {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        phone_number,
        company_name
      });
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error.response?.data || { error: 'Failed to register' };
    }
  },

  // Login user
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      // Store the JWT token in localStorage if login was successful
      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      // Check if this is an inactive account error
      if (error.response?.status === 403 && error.response?.data?.inactive) {
        // Return the inactive account data instead of throwing an error
        return error.response.data;
      }
      throw error.response?.data || { error: 'Failed to login' };
    }
  },

  // Logout user
  logout: async () => {
    try {
      // Remove the token from localStorage
      localStorage.removeItem('token');
      
      // Call the server to log the logout event (optional)
      const response = await api.get('/auth/logout');
      return response.data;
    } catch (error) {
      // Even if the server call fails, we still want to remove the token
      localStorage.removeItem('token');
      console.error('Logout error:', error);
      throw error.response?.data || { error: 'Failed to logout' };
    }
  },

  // Request password reset email
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error.response?.data || { error: 'Failed to process password reset request' };
    }
  },

  // Verify reset token validity
  verifyResetToken: async (token) => {
    try {
      const response = await api.get(`/auth/verify-reset-token/${token}`);
      return response.data;
    } catch (error) {
      console.error('Token verification error:', error);
      throw error.response?.data || { error: 'Failed to verify reset token' };
    }
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error.response?.data || { error: 'Failed to reset password' };
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return { success: false, authenticated: false };
      }
      console.error('Get user error:', error);
      throw error.response?.data || { error: 'Failed to get user data' };
    }
  }
};

// Chat services
export const chatService = {
  // Send a message to the chat API
  sendMessage: async (message, sessionId) => {
    try {
      const response = await api.post('/chat', {
        message,
        session_id: sessionId
      });
      return response.data;
    } catch (error) {
      console.error('Send message error:', error);
      if (error.response?.status === 403) {
        // Check if this is an AI usage rule violation or a policy violation
        if (error.response.data.error === 'Message blocked by AI usage rules') {
          // Enhanced AI rule violation with document reference
          return {
            success: false,
            error: 'Message blocked by AI usage rules',
            reason: error.response.data.reason,
            violatedDocument: error.response.data.violatedDocument
          };
        } else {
          // Regular policy violation
          return error.response.data;
        }
      }
      throw error.response?.data || { error: 'Failed to send message' };
    }
  },

  // Get chat history for a session
  getChatHistory: async (sessionId) => {
    try {
      const response = await api.get(`/chat/history/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Get history error:', error);
      throw error.response?.data || { error: 'Failed to get chat history' };
    }
  },

  // Get all user chat sessions
  getUserSessions: async () => {
    try {
      const response = await api.get('/chat/sessions');
      return response.data;
    } catch (error) {
      console.error('Get sessions error:', error);
      throw error.response?.data || { error: 'Failed to get user sessions' };
    }
  },

  // Delete a chat session
  deleteSession: async (sessionId) => {
    try {
      const response = await api.delete(`/chat/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Delete session error:', error);
      throw error.response?.data || { error: 'Failed to delete session' };
    }
  }
};

// AI usage statistics services
export const aiUsageService = {
  // Get system-wide AI usage statistics
  getSystemStats: async (options = {}) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (options.startDate) {
        params.append('start_date', options.startDate);
      }
      if (options.endDate) {
        params.append('end_date', options.endDate);
      }

      const queryString = params.toString();
      const url = `/ai-usage/system${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching system AI usage stats:', error);
      throw error.response?.data || { error: 'Failed to fetch AI usage statistics' };
    }
  },

  // Get AI usage statistics for the current user
  getUserStats: async (options = {}) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (options.startDate) {
        params.append('start_date', options.startDate);
      }
      if (options.endDate) {
        params.append('end_date', options.endDate);
      }

      const queryString = params.toString();
      const url = `/ai-usage/user${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user AI usage stats:', error);
      throw error.response?.data || { error: 'Failed to fetch AI usage statistics' };
    }
  },

  // Get AI usage statistics for a specific user (admin only)
  getSpecificUserStats: async (userId, options = {}) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      // Build query parameters
      const params = new URLSearchParams();
      if (options.startDate) {
        params.append('start_date', options.startDate);
      }
      if (options.endDate) {
        params.append('end_date', options.endDate);
      }

      const queryString = params.toString();
      const url = `/ai-usage/user/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error(`Error fetching AI usage stats for user ${userId}:`, error);
      throw error.response?.data || { error: 'Failed to fetch AI usage statistics' };
    }
  }
};

// Export default object
const apiService = {
  authService,
  chatService,
  aiUsageService
};

export default apiService;
